import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, first, map, Observable, of, Subject } from 'rxjs';
import { finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';
import { GungBackendFeatureService, GungFeature as CommonGungFeature } from 'gung-common';

@Injectable({
  providedIn: 'root'
})
export class BackendFeatureService extends GungBackendFeatureService {
  currentUser: string = null;
  protected features: GungFeatureMap;
  featuresSubject: Subject<GungFeatureMap> = new BehaviorSubject<GungFeatureMap>(null);
  protected pendingFetch: Observable<GungFeatureMap> = null;
  protected pendingFetchUser: string = null;

  constructor(
    protected http: HttpClient,
    protected authService: AuthService
  ) {
    super(http);
  }

  public isActivated(featureId: string): Observable<boolean> {
    return this.getAvailableFeatures().pipe(map(features => !!features[featureId]));
  }

  /**
   * @param keepOnError If true and a feature map is already loaded, a failed fetch keeps that map instead of
   * wiping it to {}. Meant for forced refreshes (e.g. route guards) so a transient failure doesn't clear the
   * app-wide map. Don't use it for login-time refreshes - the stale map could belong to the previous user.
   */
  public getAvailableFeatures(nocache?: boolean, keepOnError?: boolean): Observable<GungFeatureMap> {
    return this.authService.getCurrentUser().pipe(
      first(),
      switchMap(user => {
        const username = user?.username ?? null;
        // The backend filters features on the user's roles, so a map fetched before login (or for the previous
        // user) must never be served after it - that hid role-gated admin menu links until a reload.
        if (!nocache && this.features && this.currentUser === username) {
          return of(this.features);
        }
        if (!nocache && this.pendingFetch && this.pendingFetchUser === username) {
          return this.pendingFetch;
        }
        return this.fetchFeatures(username, keepOnError);
      })
    );
  }

  protected fetchFeatures(username: string, keepOnError?: boolean): Observable<GungFeatureMap> {
    const headers = {
      maxAge: '-1'
    };
    const fetch = this.http.get<GungFeatureMap>('public/json/features', { headers }).pipe(
      // An empty map also covers backends old enough not to have the endpoint.
      catchError(() => of(keepOnError && this.features ? this.features : {})),
      tap(features => {
        this.features = features;
        this.currentUser = username;
        this.featuresSubject.next(features);
      }),
      finalize(() => {
        if (this.pendingFetch === fetch) {
          this.pendingFetch = null;
        }
      }),
      shareReplay(1)
    );
    this.pendingFetch = fetch;
    this.pendingFetchUser = username;
    return fetch;
  }
}

export type GungFeatureMap = { [s: string]: GungFeature };
export interface GungFeature extends CommonGungFeature { }
